import { Card } from '@/types/Card';
import { CARD_KEYS, CHANNEL_KEYS, CHANNEL_NAMES } from '../constants/keys';
import { FeatureType } from '@/types/Feature';
import { cardLinkMap } from './cardLinks';
import { BANK_KEYS, BANK_NAMES } from '../constants/keys';

/**
 * Get card objects by their IDs
 * @param ids Array of card IDs or card key constants
 * @returns Array of Card objects
 */
export function getCardsByIds(ids: string[]): Card[] {
  return ids.map(id => cards[id]).filter(Boolean);
}

// Define cards using the constants as keys
export const cards: { [key: string]: Card } = {
  [CARD_KEYS.CATHAY_CASH_BACK]: {
    name: '國泰現金回饋御璽卡',
    summary: '國外消費2%現金回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.CATHAY],
    payment_network: 'VISA',
    picture: 'logo_cathay-cashback.png',
    key: 'cathay-cash-back',
    link: cardLinkMap[CARD_KEYS.CATHAY_CASH_BACK].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.CATHAY_CASH_BACK].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '0.5% 現金',
        max_feedback: '無上限',
        simplified_condition: '國內一般消費',
        condition: '國內一般消費享0.5%現金回饋，無上限',
        channel: '一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2% 現金',
        max_feedback: '無上限',
        simplified_condition: '海外消費',
        condition: '海外消費享2%現金回饋，無上限',
        channel: '海外',
      },
    ],
    domestic_feedback: '0.5%',
    international_feedback: '2%',
    specific_channel_feedback: '2%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.CATHAY_BUSINESS]: {
    name: '國泰商務御璽卡',
    summary: '商務消費3%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.CATHAY],
    payment_network: 'VISA',
    picture: 'logo_cathay-business.png',
    key: 'cathay-business',
    link: cardLinkMap[CARD_KEYS.CATHAY_BUSINESS].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.CATHAY_BUSINESS].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 現金',
        max_feedback: '每月最高回饋500元',
        simplified_condition: '商務消費',
        condition: '於指定商務類別消費享3%現金回饋，包含差旅、交通、餐飲等，每月最高回饋500元',
        channel: '商務',
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '每年2次',
        simplified_condition: 'Priority Pass',
        condition: '持卡人可享有Priority Pass會員資格，每年可免費使用全球機場貴賓室2次',
        channel: '機場貴賓室',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1.5% 現金',
        max_feedback: '無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享1.5%現金回饋，無上限',
        channel: '一般消費',
      },
    ],
    domestic_feedback: '1.5%',
    international_feedback: '1.5%',
    specific_channel_feedback: '3%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.AMERICANEXPRESS_PLAT]: {
    name: '美國運通白金卡',
    summary: '頂級旅遊禮遇、機場貴賓室無限次使用',
    issuing_bank: BANK_NAMES[BANK_KEYS.AMERICANEXPRESS],
    payment_network: 'American Express',
    picture: 'logo_americanexpress-plat.png',
    key: 'americanexpress-plat',
    link: cardLinkMap[CARD_KEYS.AMERICANEXPRESS_PLAT].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.AMERICANEXPRESS_PLAT].applyUrl,
    features: [
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '無限次使用',
        simplified_condition: 'Priority Pass會員資格',
        condition: '持卡人可獲得Priority Pass會員資格，享全球1,300多間機場貴賓室無限次使用',
        channel: '機場貴賓室',
      },
      {
        type: FeatureType.TRAVEL,
        feedback: 'NT$10,000旅遊金',
        max_feedback: '每年一次',
        simplified_condition: '指定旅遊平台預訂',
        condition: '透過美國運通旅遊服務預訂指定旅遊產品，可獲得NT$10,000旅遊金，每年一次',
        channel: '旅遊',
      },
      {
        type: FeatureType.HOTEL,
        feedback: '免費住宿',
        max_feedback: '每年一晚',
        simplified_condition: '指定豪華酒店',
        condition:
          '在Fine Hotels + Resorts®計劃中的指定豪華酒店預訂連續兩晚或以上，可獲贈一晚免費住宿',
        channel: '酒店',
      },
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$30=1哩',
        max_feedback: '無上限',
        simplified_condition: '一般消費',
        condition: '一般消費每NT$30可獲得1哩飛行里數，可轉換至多家航空公司里程計劃',
        channel: '里程',
      },
    ],
    domestic_feedback: 'NT$30=1哩',
    international_feedback: 'NT$30=1哩',
    specific_channel_feedback: '機場貴賓室無限次',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.TAISHIN_GOGO]: {
    name: '台新@GoGo卡',
    summary: '指定網購、行動支付最高3%回饋，新戶最高30%',
    issuing_bank: BANK_NAMES[BANK_KEYS.TAISHIN],
    payment_network: 'VISA/Mastercard',
    picture: 'logo_taishin-gogo.png',
    key: 'taishin-gogo',
    link: cardLinkMap[CARD_KEYS.TAISHIN_GOGO].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.TAISHIN_GOGO].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 台新Point',
        max_feedback: '每月最高回饋300點',
        simplified_condition: '指定網購平台',
        condition:
          '於指定網購平台(蝦皮shopee、momo、酷澎coupang等)消費，享3%台新Point回饋，適用期間2024/12/1~2025/8/31結帳帳單',
        channel: '網購',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 台新Point',
        max_feedback: '每月最高回饋300點',
        simplified_condition: '指定行動支付',
        condition:
          '使用指定行動支付(街口支付、Apple Pay等)消費，享3%台新Point回饋，適用期間2024/12/1~2025/8/31結帳帳單',
        channel: '行動支付',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 台新Point',
        max_feedback: '每月最高回饋300點',
        simplified_condition: '指定訂房網站',
        condition:
          '於指定國際訂房網站(Agoda、Trip.com等)消費，享3%台新Point回饋，適用期間2024/12/1~2025/8/31結帳帳單',
        channel: '訂房',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '最高10% 回饋',
        max_feedback: '優惠券不限量',
        simplified_condition: '@GoGo卡感謝祭',
        condition:
          '領券立享指定通路(百貨/全聯/超商/外送/餐飲/網購/中油/旅行社/訂房網/航空/日本)最高10%回饋，優惠券不限量',
        channel: '指定通路',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '最高30% 回饋',
        max_feedback: '新戶限定',
        simplified_condition: '新戶綁Pay加碼',
        condition:
          '新戶綁定Apple Pay/街口支付等行動支付，享最高20%加碼回饋，加計指定通路優惠券最高可達30%回饋',
        channel: '行動支付',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1.3% 回饋',
        max_feedback: '須登錄',
        simplified_condition: '保費一次付清',
        condition: '使用@GoGo卡繳交保費一次付清，享最高1.3%回饋(須登錄)',
        channel: '保險',
      },
      {
        type: FeatureType.BENEFIT,
        feedback: 'NT$2,000萬',
        max_feedback: '國內外皆適用',
        simplified_condition: '旅遊平安險',
        condition: '持卡享有最高NT$2,000萬旅遊平安險保障，國內外旅遊皆適用',
        channel: '旅遊',
      },
      {
        type: FeatureType.DISCOUNT,
        feedback: '最高14%',
        max_feedback: 'Agoda最高現折8%',
        simplified_condition: '訂房優惠',
        condition:
          '於Agoda專屬網頁刷@GoGo卡訂房享最高現折8%，Booking.com最高現折10%，Trip.com最高現折6%',
        channel: '訂房',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '2%',
    specific_channel_feedback: '10%',
    start_date: '2025-01-01',
    end_date: '2025-08-31',
  },
  [CARD_KEYS.TAISHIN_BUSINESS]: {
    name: '台新商務御璽卡',
    summary: '商務人士必備，海外最高3%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.TAISHIN],
    payment_network: 'VISA',
    picture: 'logo_taishin-business.png',
    key: 'taishin-business',
    link: cardLinkMap[CARD_KEYS.TAISHIN_BUSINESS].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.TAISHIN_BUSINESS].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 台新Point',
        max_feedback: '回饋無上限',
        simplified_condition: '海外消費',
        condition: '使用台新商務御璽卡於海外消費，享3%台新Point回饋無上限',
        channel: '海外',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2% 台新Point',
        max_feedback: '回饋無上限',
        simplified_condition: '國內航空、訂房',
        condition: '使用台新商務御璽卡於國內航空公司、訂房網站消費，享2%台新Point回饋無上限',
        channel: '旅遊',
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場接送',
        max_feedback: '每年2次',
        simplified_condition: '當月消費滿額',
        condition: '當月消費滿NT$80,000，可享免費機場接送服務，每年最多2次',
        channel: '機場接送',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '3%',
    specific_channel_feedback: '2%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.TAISHIN_TZUKUANG]: {
    name: '台新燦坤生活卡',
    summary: '燦坤消費最高10%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.TAISHIN],
    payment_network: 'Mastercard',
    picture: 'logo_taishin-tzukuang.png',
    key: 'taishin-tzukuang',
    link: cardLinkMap[CARD_KEYS.TAISHIN_TZUKUANG].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.TAISHIN_TZUKUANG].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '10% 台新Point',
        max_feedback: '每月最高回饋500點',
        simplified_condition: '燦坤消費',
        condition: '於燦坤3C實體門市或官網消費，享10%台新Point回饋，每月最高回饋500點',
        channel: '3C家電',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 台新Point',
        max_feedback: '每月最高回饋300點',
        simplified_condition: '指定超市、量販店',
        condition:
          '於指定超市、量販店(家樂福、全聯、頂好、愛買)消費，享5%台新Point回饋，每月最高回饋300點',
        channel: '超市',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2% 台新Point',
        max_feedback: '每月最高回饋200點',
        simplified_condition: '指定加油站',
        condition: '於指定加油站(中油、台塑)消費，享2%台新Point回饋，每月最高回饋200點',
        channel: '加油',
      },
    ],
    domestic_feedback: '0.5%',
    international_feedback: '1.5%',
    specific_channel_feedback: '10%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.TAISHIN_GOGORO]: {
    name: '台新Gogoro Rewards聯名卡',
    summary: 'Gogoro資費最高10%回饋，海外最高4%',
    issuing_bank: BANK_NAMES[BANK_KEYS.TAISHIN],
    payment_network: 'VISA',
    picture: 'logo_taishin-gogoro.png',
    key: 'taishin-gogoro',
    link: cardLinkMap[CARD_KEYS.TAISHIN_GOGORO].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.TAISHIN_GOGORO].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '10% Gogoro Points',
        max_feedback: '回饋無上限',
        simplified_condition: '電池資費帳單',
        condition:
          '使用台新Gogoro Rewards聯名卡繳納Gogoro電池資費帳單，最高享10% Gogoro Smart Points回饋無上限',
        channel: '電池資費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '15% Gogoro Points',
        max_feedback: '新戶首刷禮',
        simplified_condition: 'GoShare消費',
        condition: '使用台新Gogoro Rewards聯名卡於GoShare消費，最高享15% Gogoro Smart Points回饋',
        channel: '共享機車',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '4% Gogoro Points',
        max_feedback: '回饋無上限',
        simplified_condition: '海外消費',
        condition: '使用台新Gogoro Rewards聯名卡於海外消費，最高享4% Gogoro Smart Points回饋無上限',
        channel: '海外消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% Gogoro Points',
        max_feedback: '回饋無上限',
        simplified_condition: '指定通路消費',
        condition:
          '使用台新Gogoro Rewards聯名卡於指定超商/高鐵/外送平台/旅遊平台消費，最高享3% Gogoro Smart Points回饋',
        channel: '指定通路',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% Gogoro Points',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '使用台新Gogoro Rewards聯名卡一般消費，最高享1% Gogoro Smart Points回饋無上限',
        channel: '一般消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '4%',
    specific_channel_feedback: '10%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.TAISHIN_JKOS]: {
    name: '台新街口聯名卡',
    summary: '一般消費1%街口幣無上限',
    issuing_bank: BANK_NAMES[BANK_KEYS.TAISHIN],
    payment_network: 'VISA',
    picture: 'logo_taishin-jkos.gif',
    key: 'taishin-jkos',
    link: cardLinkMap[CARD_KEYS.TAISHIN_JKOS].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.TAISHIN_JKOS].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 街口幣',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '使用街口支付APP綁定台新街口聯名卡，一般消費(含街口繳費)享1%街口幣無上限',
        channel: '一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '7.5% 街口幣',
        max_feedback: '回饋無上限',
        simplified_condition: '日本PayPay交易',
        condition:
          '使用街口支付APP綁定台新街口聯名卡，日本PayPay交易享1%街口幣無上限，再享街口活動加碼最高5%街口幣，且免1.5%海外交易手續費',
        channel: '海外消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 街口幣',
        max_feedback: '回饋無上限',
        simplified_condition: '指定通路消費',
        condition:
          '於街口支付App綁定台新街口聯名卡，於量販、KLOOK、91APP等百大合作通路消費，享3%街口幣無上限',
        channel: '指定通路',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '14% 街口幣',
        max_feedback: '鈦金商務卡新戶獨享',
        simplified_condition: '日本PayPay交易',
        condition: '街口支付App綁定新申辦街口Mastercard鈦金商務卡，日本PayPay交易享最高14%',
        channel: '海外消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '1%',
    specific_channel_feedback: '14%',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.FUBON_DIGITAL]: {
    name: '富邦數位生活卡',
    summary: '網購最高10%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.FUBON],
    payment_network: 'Mastercard',
    picture: 'logo_fubon-digital.png',
    key: 'fubon-digital',
    link: cardLinkMap[CARD_KEYS.FUBON_DIGITAL].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.FUBON_DIGITAL].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '10% 現金',
        max_feedback: '每月最高回饋500元',
        simplified_condition: '指定電商平台',
        condition: '指定電商平台消費享10%現金回饋，每月最高回饋500元',
        channel: '網購',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 現金',
        max_feedback: '每月最高回饋300元',
        simplified_condition: '一般網購',
        condition: '一般網購消費享5%現金回饋，每月最高回饋300元',
        channel: '網購',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享1%現金回饋，無上限',
        channel: '國內一般消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '1%',
    specific_channel_feedback: '10%',
    start_date: '2025-01-01',
    end_date: '2025-08-31',
  },
  [CARD_KEYS.TAISHIN_FLYGO]: {
    name: '台新FlyGo鈦金商務卡',
    summary: '航旅最高5%、海外最高3%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.TAISHIN],
    payment_network: 'Mastercard',
    picture: 'logo_taishin-flygo.png',
    key: 'taishin-flygo',
    link: cardLinkMap[CARD_KEYS.TAISHIN_FLYGO].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.TAISHIN_FLYGO].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 台新Point',
        max_feedback: '回饋無上限',
        simplified_condition: '精選航旅',
        condition:
          '精選航旅消費享最高5%台新Point回饋，包含華航、長榮、星宇、虎航、國泰航空、華信、立榮、Klook、東南旅遊',
        channel: '旅遊',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 台新Point',
        max_feedback: '回饋無上限',
        simplified_condition: '海外消費',
        condition: '海外消費(含網購)享最高3%台新Point回饋，無上限',
        channel: '海外消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '0.3% 台新Point',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享0.3%台新Point回饋，無上限',
        channel: '國內一般消費',
      },
    ],
    domestic_feedback: '0.3%',
    international_feedback: '3%',
    specific_channel_feedback: '5%',
    start_date: '2024-12-01',
    end_date: '2025-08-31',
  },
  [CARD_KEYS.ESUN_UBEAR]: {
    name: '玉山U Bear信用卡',
    summary: '網購最高3%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.ESUN],
    payment_network: 'Mastercard',
    picture: 'logo_esun-ubear.webp',
    key: 'esun-ubear',
    link: cardLinkMap[CARD_KEYS.ESUN_UBEAR].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.ESUN_UBEAR].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% 現金',
        max_feedback: '每期回饋上限100元，於當期帳單直接折抵',
        simplified_condition: '指定娛樂/影音平台消費享10%現金回饋',
        condition:
          '指定娛樂/影音平台包含Disney+、Nintendo、PlayStation、Netflix、Spotify等串流平台',
        channel: '娛樂/影音平台',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 現金',
        max_feedback: '每月最高回饋400元',
        simplified_condition: '行動支付、網購最高10%回饋',
        condition:
          '網購最高享3%現金回饋 (含基本回饋1%+網購加碼2%)，加碼2%需綁定帳單e化。網路消費包含國內外線上消費 (例如：momo購物網、蝦皮、Coupang 酷澎、淘寶、線上訂房、高鐵線上購票等)、行動支付 (例如：LINE Pay、街口支付等)',
        channel: '網購',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '國內外一般消費享1%現金回饋',
        condition: '國內外一般消費享1%現金回饋，無上限',
        channel: '國內外一般消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '1%',
    specific_channel_feedback: '6%',
    start_date: '2025-01-01',
    end_date: '2025-08-31',
  },
  [CARD_KEYS.ESUN_PI]: {
    name: '玉山Pi拍錢包聯名卡',
    summary: '外送最高10%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.ESUN],
    payment_network: 'VISA',
    picture: 'logo_esun-pi.webp',
    key: 'esun-pi',
    link: cardLinkMap[CARD_KEYS.ESUN_PI].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.ESUN_PI].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '10% 現金',
        max_feedback: '每月最高回饋300元',
        simplified_condition: '指定外送平台',
        condition: '指定外送平台消費享10%現金回饋，每月最高回饋300元',
        channel: '外送',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 現金',
        max_feedback: '每月最高回饋250元',
        simplified_condition: '一般餐廳',
        condition: '一般餐廳消費享5%現金回饋，每月最高回饋250元',
        channel: '餐飲',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1.5% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享1.5%現金回饋，無上限',
        channel: '國內一般消費',
      },
    ],
    domestic_feedback: '1.5%',
    international_feedback: '1.5%',
    specific_channel_feedback: '10%',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.FUBON_J]: {
    name: '台北富邦J卡',
    summary: '日韓泰旅遊最高8%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.FUBON],
    payment_network: 'JCB',
    picture: 'logo_fubon-j.webp',
    key: 'fubon-j',
    link: cardLinkMap[CARD_KEYS.FUBON_J].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.FUBON_J].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% 現金',
        max_feedback: '每季最高回饋600元',
        simplified_condition: '日韓實體消費',
        condition: '日韓實體消費享3%無上限+加碼3%回饋，每季最高回饋600元，需登錄',
        channel: '海外',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% 現金',
        max_feedback: '每季最高回饋600元',
        simplified_condition: '泰國實體消費',
        condition: '泰國實體消費享1%無上限+加碼5%回饋，每季最高回饋600元，需登錄',
        channel: '海外',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '10% 現金',
        max_feedback: '每季最高回饋200元',
        simplified_condition: '日韓指定便利商店',
        condition:
          '日本7-11、LAWSON、FAMILY MART及韓國emart24、GS25、CU便利商店消費享10%回饋，每季最高回饋200元，需登錄',
        channel: '海外',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% 現金',
        max_feedback: '每月最高回饋500元',
        simplified_condition: '指定航空、旅行社',
        condition:
          '中華航空、長榮航空、星宇航空、台灣虎航及易遊網、可樂旅遊、雄獅旅遊、東南旅遊、山富旅遊消費享6%回饋，每月最高回饋500元，需登錄',
        channel: '旅遊',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% 現金',
        max_feedback: '每月最高回饋100元',
        simplified_condition: 'foodpanda',
        condition: '透過foodpanda官網/APP消費享6%回饋，每月最高回饋100元，需登錄',
        channel: '外送',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% 現金',
        max_feedback: '每月回饋上限依活動而定',
        simplified_condition: '指定速食店、手搖飲',
        condition: '指定速食店、手搖飲消費享6%回饋，每月回饋上限依活動而定，需登錄',
        channel: '餐飲',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享1%現金回饋，無上限',
        channel: '國內一般消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '3%',
    specific_channel_feedback: '8%',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.FUBON_MOMO]: {
    name: '富邦momo卡',
    summary: 'momo通路最高3%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.FUBON],
    payment_network: 'Mastercard',
    picture: 'logo_fubon-momo.webp',
    key: 'fubon-momo',
    link: cardLinkMap[CARD_KEYS.FUBON_MOMO].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.FUBON_MOMO].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% momo幣',
        max_feedback: '每期帳單回饋上限1,000 momo幣',
        simplified_condition: 'momo通路消費',
        condition:
          '於momo購物網、看看買、型錄、mo店+消費享3% momo幣回饋，每期帳單回饋上限1,000 momo幣',
        channel: '網購',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% momo幣',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享1% momo幣回饋，無上限',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% momo幣',
        max_feedback: '回饋無上限',
        simplified_condition: '海外消費',
        condition: '海外消費享1% momo幣回饋，無上限',
        channel: '海外',
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '前3期免息',
        max_feedback: '12期以上分期',
        simplified_condition: '海外消費',
        condition: '海外消費單筆分期享12期以上前三期免息優惠',
        channel: '海外',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '0.5% momo幣',
        max_feedback: '回饋無上限',
        simplified_condition: '保費',
        condition: '保費消費享0.5% momo幣回饋或最高12期分期0利率二選一',
        channel: '保險',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '1%',
    specific_channel_feedback: '3%',
    start_date: '2023-12-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.FUBON_LIFESTYLE]: {
    name: '富邦富利生活卡',
    summary: '8大生活消費5倍紅利',
    issuing_bank: BANK_NAMES[BANK_KEYS.FUBON],
    payment_network: 'VISA',
    picture: 'logo_fubon-lifestyle.webp',
    key: 'fubon-lifestyle',
    link: cardLinkMap[CARD_KEYS.FUBON_LIFESTYLE].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.FUBON_LIFESTYLE].applyUrl,
    features: [
      {
        type: FeatureType.BENEFIT,
        feedback: '5倍紅利',
        max_feedback: '回饋無上限',
        simplified_condition: '8大生活消費通路',
        condition:
          '於百貨、量販、超市、餐廳、加油、書店、旅行社、航空公司等8大通路消費享5倍紅利回饋(NT$20=5點)，回饋無上限',
        channel: '百貨',
      },
      {
        type: FeatureType.INSTALLMENT,
        feedback: '前3期免息',
        max_feedback: '海外消費單筆分期',
        simplified_condition: '海外消費',
        condition: '海外消費單筆分期享前3期免息優惠',
        channel: '海外',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '300元刷卡金',
        max_feedback: '指定通路分期',
        simplified_condition: '旅行社、免稅店',
        condition: '指定旅行社、免稅店分期加碼送300元刷卡金',
        channel: '旅遊',
      },
      {
        type: FeatureType.DISCOUNT,
        feedback: '最高220元折抵',
        max_feedback: '外送/生鮮雜貨',
        simplified_condition: 'Foodpanda',
        condition: 'Foodpanda外送/生鮮雜貨最高折抵220元',
        channel: '外送',
      },
    ],
    domestic_feedback: '1倍紅利',
    international_feedback: '1倍紅利',
    specific_channel_feedback: '5倍紅利',
    start_date: '2023-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.FUBON_COSTCO]: {
    name: '富邦Costco聯名卡',
    summary: 'Costco店內2%好多金回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.FUBON],
    payment_network: 'Mastercard',
    picture: 'logo_fubon-costco.jpg',
    key: 'fubon-costco',
    link: cardLinkMap[CARD_KEYS.FUBON_COSTCO].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.FUBON_COSTCO].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '2% 好多金',
        max_feedback: '回饋無上限',
        simplified_condition: 'Costco店內消費',
        condition: '於台灣Costco店內消費享2%好多金回饋無上限',
        channel: '量販',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 好多金',
        max_feedback: '回饋無上限',
        simplified_condition: 'Costco線上購物',
        condition: '於Costco線上購物享3%好多金回饋無上限',
        channel: '網購',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 好多金',
        max_feedback: '回饋無上限',
        simplified_condition: 'Costco自助加油站',
        condition: '於Costco自助加油站消費享3%好多金回饋無上限',
        channel: '加油',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 好多金',
        max_feedback: '回饋無上限',
        simplified_condition: '店外一般消費',
        condition: '店外一般消費享1%好多金回饋無上限',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '0.5% 好多金',
        max_feedback: '回饋無上限',
        simplified_condition: '保費消費',
        condition: '保費消費享0.5%好多金回饋無上限或最高12期分期0利率二選一',
        channel: '保險',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '1%',
    specific_channel_feedback: '3%',
    start_date: '2023-08-08',
    end_date: '2026-12-31',
  },
  [CARD_KEYS.FUBON_INSURANCE]: {
    name: '富邦鑽保卡',
    summary: '保費最高12期分期0利率',
    issuing_bank: BANK_NAMES[BANK_KEYS.FUBON],
    payment_network: 'VISA',
    picture: 'logo_fubon-insurance.jpg',
    key: 'fubon-insurance',
    link: cardLinkMap[CARD_KEYS.FUBON_INSURANCE].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.FUBON_INSURANCE].applyUrl,
    features: [
      {
        type: FeatureType.DISCOUNT,
        feedback: '1% 折扣',
        max_feedback: '富邦人壽保費',
        simplified_condition: '富邦人壽保費',
        condition: '扣繳富邦人壽保費享1%折扣，部分商品無法享有優惠折扣',
        channel: '保險',
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '最高12期分期0利率',
        max_feedback: '單筆5萬元以上',
        simplified_condition: '保費交易',
        condition:
          '保費交易可享最高12期分期0利率或0.5%現金回饋二選一，114/4/1起設定9期或12期自動分期優惠，單筆保費金額需達新台幣5萬元',
        channel: '保險',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '0.7% 現金回饋',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享0.7%現金回饋，回饋無上限',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.INSTALLMENT,
        feedback: '前3期免息',
        max_feedback: '海外消費單筆分期',
        simplified_condition: '海外消費',
        condition: '海外消費單筆分期享前3期免息優惠',
        channel: '海外',
      },
    ],
    domestic_feedback: '0.7%',
    international_feedback: '0.7%',
    specific_channel_feedback: '1%',
    start_date: '2024-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.SINO_DAWHO]: {
    name: '永豐大戶現金回饋卡',
    summary: '指定通路 國內7% / 國外8%',
    issuing_bank: BANK_NAMES[BANK_KEYS.SINO],
    payment_network: 'VISA',
    picture: 'logo_sino-dawho.png',
    key: 'sino-dawho',
    link: cardLinkMap[CARD_KEYS.SINO_DAWHO].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.SINO_DAWHO].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '2% 現金',
        max_feedback: '最高回饋 600 現金',
        simplified_condition: '國內基本回饋',
        condition: '此優惠僅「大戶等級」才可享有',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 現金',
        max_feedback: '最高回饋 600 現金',
        simplified_condition: '大戶等級',
        condition: '此優惠僅「大戶等級」才可享有',
        channel: '海外一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '8% 現金',
        max_feedback: '最高回饋 900 現金',
        simplified_condition: '大戶等級',
        condition:
          '此優惠僅「大戶等級」才可享有，海外最高8%/國內最高7%（2% 海外基本回饋 + 1% 大戶加碼 + 5% 指定通路加碼）',
        channel: '海外一般消費',
      },
    ],
    domestic_feedback: '1.5%',
    international_feedback: '4.5%',
    specific_channel_feedback: '5.5%',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.TAISHIN_GIVING]: {
    name: '台新玫瑰Giving卡',
    summary: '海外3%、節假日2%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.TAISHIN],
    payment_network: 'Mastercard/VISA',
    picture: 'logo_taishin-giving.gif',
    key: 'taishin-giving',
    link: cardLinkMap[CARD_KEYS.TAISHIN_GIVING].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.TAISHIN_GIVING].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '2% 台新Point',
        max_feedback: '回饋無上限',
        simplified_condition: '節假日消費',
        condition:
          '節假日消費享最高2%台新Point回饋，需申辦「台新信用卡數位帳單」及設定「台新帳戶扣繳台新信用卡費」',
        channel: '國內節假日',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 台新Point',
        max_feedback: '回饋無上限',
        simplified_condition: '海外消費',
        condition:
          '海外消費享最高3%台新Point回饋，需申辦「台新信用卡數位帳單」及設定「台新帳戶扣繳台新信用卡費」',
        channel: '海外消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 台新Point',
        max_feedback: '回饋無上限',
        simplified_condition: '平日消費',
        condition:
          '平日消費享1%台新Point回饋，需申辦「台新信用卡數位帳單」及設定「台新帳戶扣繳台新信用卡費」',
        channel: '國內平日',
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '居家清潔服務',
        max_feedback: '每年上限２次',
        simplified_condition: '平日消費滿額',
        condition: '國內平日消費滿ＮＴ＄９萬享居家清潔服務一次，每年上限２次',
        channel: '居家清潔',
      },
    ],
    domestic_feedback: '2%',
    international_feedback: '3%',
    specific_channel_feedback: '3%',
    start_date: '2025-01-01',
    end_date: '2025-08-31',
  },
  [CARD_KEYS.DBS_TRAVEL_WORLD_ELITE]: {
    name: '星展飛行世界卡世界卡',
    summary: '海外消費2.5%回饋，指定通路最高5%',
    issuing_bank: BANK_NAMES[BANK_KEYS.DBS],
    payment_network: 'Mastercard',
    picture: 'logo_dbs-travel-world-elite.webp',
    key: 'dbs-travel-world-elite',
    link: cardLinkMap[CARD_KEYS.DBS_TRAVEL_WORLD_ELITE].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.DBS_TRAVEL_WORLD_ELITE].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '2.5% 現金回饋',
        max_feedback: '無上限',
        simplified_condition: '海外消費',
        condition: '海外消費享2.5%現金回饋，無上限',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 回饋',
        max_feedback: '每月最高回饋1,000元',
        simplified_condition: '指定旅遊平台',
        condition: '於Agoda、Booking.com、Klook等指定旅遊平台消費享5%回饋，每月最高回饋1,000元',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '每年6次',
        simplified_condition: 'Priority Pass',
        condition: '持卡人可享有Priority Pass會員資格，每年可免費使用全球機場貴賓室6次',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '2.5%',
    specific_channel_feedback: '5%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.DBS_FUNLIFE]: {
    name: '星展豐盛無限卡',
    summary: '指定通路最高5%回饋，機場接送服務',
    issuing_bank: BANK_NAMES[BANK_KEYS.DBS],
    payment_network: 'Mastercard',
    picture: 'logo_dbs-funlife.webp',
    key: 'dbs-funlife',
    link: cardLinkMap[CARD_KEYS.DBS_FUNLIFE].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.DBS_FUNLIFE].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 回饋',
        max_feedback: '每月最高回饋1,500元',
        simplified_condition: '指定餐廳、百貨公司',
        condition: '於指定高級餐廳、百貨公司消費享5%回饋，每月最高回饋1,500元',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.DINING],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '機場接送',
        max_feedback: '每年4次',
        simplified_condition: '單筆消費滿NT$10,000',
        condition: '刷卡單筆消費滿NT$10,000，可享每年4次機場接送服務',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRANSPORTATION],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '無限次使用',
        simplified_condition: 'Priority Pass',
        condition: '持卡人可享有Priority Pass會員資格，享全球1,300多間機場貴賓室無限次使用',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
    ],
    domestic_feedback: '1.5%',
    international_feedback: '2%',
    specific_channel_feedback: '5%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.DBS_TRAVEL_WORLD]: {
    name: '星展飛行世界卡',
    summary: '海外消費2%回饋，指定旅遊通路3%',
    issuing_bank: BANK_NAMES[BANK_KEYS.DBS],
    payment_network: 'VISA',
    picture: 'logo_dbs-travel-world.webp',
    key: 'dbs-travelworld',
    link: cardLinkMap[CARD_KEYS.DBS_TRAVEL_WORLD].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.DBS_TRAVEL_WORLD].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '2% 現金回饋',
        max_feedback: '無上限',
        simplified_condition: '海外消費',
        condition: '海外消費享2%現金回饋，無上限',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 回饋',
        max_feedback: '每月最高回饋800元',
        simplified_condition: '指定旅遊平台',
        condition: '於Agoda、Booking.com、Klook等指定旅遊平台消費享3%回饋，每月最高回饋800元',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '每年2次',
        simplified_condition: 'Priority Pass',
        condition: '持卡人可享有Priority Pass會員資格，每年可免費使用全球機場貴賓室2次',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
    ],
    domestic_feedback: '0.8%',
    international_feedback: '2%',
    specific_channel_feedback: '3%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.DBS_ECO]: {
    name: '星展eco永續卡',
    summary: '指定日本商店消費8.5%優惠',
    issuing_bank: BANK_NAMES[BANK_KEYS.DBS],
    payment_network: 'VISA',
    picture: 'logo_dbs-eco.jpeg',
    key: 'dbs-eco',
    link: cardLinkMap[CARD_KEYS.DBS_ECO].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.DBS_ECO].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '最高9.8%回饋',
        max_feedback: '回饋無上限，於當期帳單直接折抵',
        simplified_condition: '日韓泰/新加坡／美歐洲 最高9.8%回饋',
        condition: '日韓泰／新加坡／美洲／歐洲一般消費享9.8%回饋，無上限',
        channel: '日韓泰／新加坡／美洲／歐洲一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '國內/外一般消費 最高6%回饋',
        condition: '',
        channel: '國內/外一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1.2% 現金積點回饋',
        max_feedback: '回饋無上限',
        simplified_condition: '國內/外一般消費享 1.2% 現金積點回饋',
        condition: '國內/外一般消費享 1.2% 現金積點回饋',
        channel: '國內/外一般消費',
      },
    ],
    domestic_feedback: '1.5%',
    international_feedback: '2.5%',
    specific_channel_feedback: '3%',
    start_date: '2025-01-01',
    end_date: '2025-08-31',
  },
  [CARD_KEYS.ESUN_KUMAMON]: {
    name: '玉山熊本熊卡',
    summary: '指定日本商店8.5%優惠',
    issuing_bank: BANK_NAMES[BANK_KEYS.ESUN],
    payment_network: 'VISA',
    picture: 'logo_esun-kumamon.webp',
    key: 'esun-kumamon',
    link: cardLinkMap[CARD_KEYS.ESUN_KUMAMON].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.ESUN_KUMAMON].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '8.5% 優惠',
        max_feedback: '加碼6%每歸戶每期回饋上限500元',
        simplified_condition: '指定日本商店消費',
        condition:
          '指定日本商店消費最高享8.5%優惠，含日本一般消費1%+指定日本商店加碼6%+雙幣卡免收1.5%國外交易服務費',
        channel: '日本消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '日本一般消費',
        condition:
          '日本一般消費享1%現金回饋，回饋無上限，於當期帳單直接折扣，雙幣卡免收1.5%國外交易服務費',
        channel: '日本消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '國內一般消費',
        condition: '國內一般消費享1%現金回饋，回饋無上限，於當期帳單直接折扣',
        channel: '國內消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '2.5%',
    specific_channel_feedback: '8.5%',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.UBOT_JIHO]: {
    name: '聯邦吉鶴卡',
    summary: '日本消費最高4%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.UBOT],
    payment_network: 'JCB',
    picture: 'logo_ubot-jiho.png',
    key: 'ubot-jiho',
    link: cardLinkMap[CARD_KEYS.UBOT_JIHO].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.UBOT_JIHO].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '4% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '日本一般消費',
        condition:
          '日本一般消費使用聯邦吉鶴卡綁定 Apple Pay並於QUICPay場域感應交易，享活動加碼1.5%回饋(月上限1,000元)，最高4%回饋。',
        channel: '海外一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1.5% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '基本回饋',
        condition: '基本回饋',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2.5% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '海外一般消費',
        condition: '海外一般消費基本回饋',
        channel: '海外一般消費',
      },
    ],
    domestic_feedback: '1.5%',
    international_feedback: '4%',
    specific_channel_feedback: '4%',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.TAISHIN_SUNROSE]: {
    name: '台新太陽/玫瑰卡(切換刷)',
    summary: '指定消費最高3.3%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.TAISHIN],
    payment_network: 'Mastercard/JCB',
    picture: 'logo_taishin-sunrose.gif',
    key: 'taishin-sunrose',
    link: cardLinkMap[CARD_KEYS.TAISHIN_SUNROSE].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.TAISHIN_SUNROSE].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '3.3% 台新Point',
        max_feedback: '每期帳單回饋上限10,000點',
        simplified_condition: '天天刷方案',
        condition:
          '天天刷方案適用超商、便利商店、加油站、指定百貨、指定訂房旅遊消費，最高3.3%台新Point回饋',
        channel: '日常消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3.3% 台新Point',
        max_feedback: '每期帳單回饋上限10,000點',
        simplified_condition: '大筆刷方案',
        condition:
          '大筆刷方案適用單筆10,000元以上消費，包含指定百貨、指定訂房旅遊消費，最高3.3%台新Point回饋',
        channel: '大額消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3.3% 台新Point',
        max_feedback: '每期帳單回饋上限10,000點',
        simplified_condition: '好饗刷方案',
        condition: '好饗刷方案適用餐飲店家、餐廳、外送平台消費，最高3.3%台新Point回饋',
        channel: '餐飲',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3.3% 台新Point',
        max_feedback: '每期帳單回饋上限10,000點',
        simplified_condition: '海外指定國家消費',
        condition: '在日本、韓國、歐洲、美國等指定國家消費，免切換即享最高3.3%台新Point回饋',
        channel: '海外消費',
      },
    ],
    domestic_feedback: '0.3%',
    international_feedback: '3.3%',
    specific_channel_feedback: '3.3%',
    start_date: '2025-01-02',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.UBOT_MCARD]: {
    name: '聯邦幸福M卡',
    summary: '交通旅遊最高6%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.UBOT],
    payment_network: 'VISA/Mastercard',
    picture: 'logo_ubot-mcard.png',
    key: 'ubot-mcard',
    link: cardLinkMap[CARD_KEYS.UBOT_MCARD].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.UBOT_MCARD].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% 現金回饋',
        max_feedback: '每月5,000元內享6%，超過享1%無上限',
        simplified_condition: '台鐵、高鐵、客運、租車',
        condition:
          '申辦電子帳單，台鐵、高鐵、指定客運(國光、統聯、和欣、阿羅哈、葛瑪蘭)、租車(聯邦租車、和運租車、格上租車)消費，每月5,000元內享6%回饋，超過享1%無上限',
        channel: '交通',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% 現金回饋',
        max_feedback: '每月5,000元內享6%，超過享1%無上限',
        simplified_condition: '航空公司、訂房網、旅行社',
        condition:
          '申辦電子帳單，航空公司(華航、長榮、星宇等)、國內飯店、訂房網及旅遊平台(Booking.com、Agoda、Hotels.com等)、旅行社(東南、山富、五福、雄獅等)消費，每月5,000元內享6%回饋，超過享1%無上限',
        channel: '旅遊',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 現金回饋',
        max_feedback: '無上限',
        simplified_condition: '海外實體商店',
        condition: '申辦電子帳單，海外實體商店消費享3%回饋無上限',
        channel: '海外消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1.2% 現金回饋',
        max_feedback: '每戶最高回饋10,000元',
        simplified_condition: '保險費',
        condition:
          '申辦電子帳單，繳納保險費享1.2%回饋，每戶最高回饋10,000元，或可選擇12期0利率分期',
        channel: '保險',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 現金回饋',
        max_feedback: '無上限',
        simplified_condition: '一般消費',
        condition: '申辦電子帳單，國內一般消費享1%回饋無上限',
        channel: '一般消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '3%',
    specific_channel_feedback: '6%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.CATHAY_SHOPEE]: {
    name: '國泰世華蝦皮購物聯名卡',
    summary: '蝦皮消費最高10%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.CATHAY],
    payment_network: 'VISA',
    picture: 'logo_cathay-shopee.png',
    key: 'cathay-shopee',
    link: cardLinkMap[CARD_KEYS.CATHAY_SHOPEE].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.CATHAY_SHOPEE].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '10% 蝦幣回饋',
        max_feedback: '依活動條件而定',
        simplified_condition: '蝦皮購物指定活動期間',
        condition:
          '於指定期間內蝦皮購物消費，基本回饋最高4%加上活動期間加碼回饋6%，總回饋率上看10%',
        channel: '網購',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2% 蝦幣回饋',
        max_feedback: '每帳單年度最高20,000蝦幣',
        simplified_condition: '蝦皮購物消費滿3,000元',
        condition: '於蝦皮購物消費滿3,000元，享2%蝦幣回饋，未滿3,000元享1%回饋',
        channel: '網購',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '0.5% 蝦幣回饋',
        max_feedback: '每帳單年度最高20,000蝦幣',
        simplified_condition: '一般消費',
        condition: '非蝦皮購物的國內消費，每筆皆可獲得0.5%蝦幣回饋',
        channel: '一般消費',
      },
      {
        type: FeatureType.DISCOUNT,
        feedback: '免運券',
        max_feedback: '每月1張',
        simplified_condition: '每月消費滿5,000元或累積5筆消費',
        condition: '每月消費滿5,000元或任意金額累積5筆成功交易訂單，次月可獲得1張蝦皮店到店免運券',
        channel: '網購',
      },
      {
        type: FeatureType.DISCOUNT,
        feedback: '海外消費免手續費',
        max_feedback: '無上限',
        simplified_condition: '海外消費',
        condition: '海外消費可享免手續費優惠，並可同步獲得蝦幣點數站外消費回饋',
        channel: '海外消費',
      },
    ],
    domestic_feedback: '0.5%',
    international_feedback: '0.5%',
    specific_channel_feedback: '10%',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.CATHAY_EVA]: {
    name: '國泰世華長榮航空聯名卡',
    summary: '指定消費NT$10=1哩，首刷最高50,000哩',
    issuing_bank: BANK_NAMES[BANK_KEYS.CATHAY],
    payment_network: 'VISA',
    picture: 'logo_cathay-eva.png',
    key: 'cathay-eva',
    link: cardLinkMap[CARD_KEYS.CATHAY_EVA].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.CATHAY_EVA].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: 'NT$10=1哩',
        max_feedback: '依卡等而定',
        simplified_condition: '哩程加速器指定消費',
        condition:
          '長榮官網/EVA Mobile app 購票、國內旅行社購買長榮機票、國外自由行/團體旅遊、國外消費、機上免稅品',
        channel: '指定通路',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: 'NT$15-30=1哩',
        max_feedback: '依卡等而定',
        simplified_condition: '一般消費',
        condition:
          '一般國內消費或國外消費，極致無限卡NT$15=1哩，無限卡NT$20=1哩，御璽卡NT$25=1哩，白金卡NT$30=1哩',
        channel: '一般消費',
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '生日加倍哩程',
        max_feedback: '最高額外100%哩程',
        simplified_condition: '生日月消費',
        condition: '生日當月哩程加速器特店消費，里數加倍享額外100%回饋',
        channel: '指定通路',
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '首刷禮',
        max_feedback: '極致無限卡最高50,000哩',
        simplified_condition: '核卡後60天內消費達標',
        condition: '極致無限卡核卡後60天內消費NT$50,000可獎勵5萬哩',
        channel: '新戶優惠',
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '機場接送服務',
        max_feedback: '極致無限卡每年6次',
        simplified_condition: '極致無限卡持卡人',
        condition: '極致無限卡持卡人每年可享6次免費機場接送服務，無限卡每年2次',
        channel: '旅遊',
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '機場貴賓室',
        max_feedback: '極致無限卡適用',
        simplified_condition: '極致無限卡持卡人',
        condition: '極致無限卡持卡人可享龍騰卡全球機場貴賓室服務',
        channel: '旅遊',
      },
    ],
    domestic_feedback: 'NT$15-30=1哩',
    international_feedback: 'NT$15-30=1哩',
    specific_channel_feedback: 'NT$10=1哩',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.CATHAY_ASIA_MILES]: {
    name: '國泰世華亞洲萬里通聯名卡',
    summary: '最高NT$10=1哩，生日月消費2倍里數',
    issuing_bank: BANK_NAMES[BANK_KEYS.CATHAY],
    payment_network: 'VISA',
    picture: 'logo_cathay-asia-miles.png',
    key: 'cathay-asia-miles',
    link: cardLinkMap[CARD_KEYS.CATHAY_ASIA_MILES].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.CATHAY_ASIA_MILES].applyUrl,
    features: [
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$10=1哩',
        max_feedback: '世界卡每月最高NT$15萬消費',
        simplified_condition: '哩程加速器指定商店消費',
        condition: '於哩程加速器指定商店消費，世界卡/鈦商卡享NT$10=1哩，白金卡享NT$15=1哩',
        channel: '指定通路',
      },
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$5=1哩',
        max_feedback: '最高額外回饋5,000哩',
        simplified_condition: '生日當月哩程加速器消費',
        condition: '生日當月於哩程加速器特店消費，里數加倍享2倍回饋，最高享NT$5=1哩',
        channel: '指定通路',
      },
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$22=1哩',
        max_feedback: '無上限',
        simplified_condition: '世界卡一般消費',
        condition: '世界卡一般國內消費或國外消費，每NT$22可獲得1哩',
        channel: '一般消費',
      },
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$25=1哩',
        max_feedback: '無上限',
        simplified_condition: '鈦商卡一般消費',
        condition: '鈦商卡一般國內消費或國外消費，每NT$25可獲得1哩',
        channel: '一般消費',
      },
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$30=1哩',
        max_feedback: '無上限',
        simplified_condition: '白金卡/里享卡一般消費',
        condition: '白金卡/里享卡一般國內消費或國外消費，每NT$30可獲得1哩',
        channel: '一般消費',
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場接送服務',
        max_feedback: '世界卡每年2次',
        simplified_condition: '世界卡持卡人',
        condition: '世界卡持卡人每年可享2次免費機場接送服務',
        channel: '旅遊',
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '世界卡每年6次',
        simplified_condition: '世界卡持卡人',
        condition: '世界卡持卡人每年可享6次免費機場貴賓室禮遇',
        channel: '旅遊',
      },
    ],
    domestic_feedback: 'NT$22-30=1哩',
    international_feedback: 'NT$22-30=1哩',
    specific_channel_feedback: 'NT$10=1哩',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.CATHAY_CUBE]: {
    name: '國泰世華CUBE卡',
    summary: '隨選回饋 有它就足夠｜日本賞｜來支付｜玩數位｜樂饗購｜趣旅行｜集精選｜慶生月',
    issuing_bank: BANK_NAMES[BANK_KEYS.CATHAY],
    payment_network: 'VISA',
    picture: 'logo_cathay-cube.png',
    key: 'cathay-cube',
    link: cardLinkMap[CARD_KEYS.CATHAY_CUBE].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.CATHAY_CUBE].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '3.3% 小樹點(信用卡)',
        max_feedback: '回饋無上限',
        simplified_condition: '趣旅行權益',
        condition: '選擇《趣旅行》權益方案，享指定消費最高3.3%小樹點(信用卡)回饋無上限',
        channel: '旅遊',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2% 小樹點(信用卡)',
        max_feedback: '回饋無上限',
        simplified_condition: 'LINE Pay消費',
        condition: '切換至《來支付》權益方案，享LINE Pay消費2%小樹點(信用卡)回饋',
        channel: '行動支付',
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '權益分級',
        max_feedback: '三級優惠',
        simplified_condition: '依持卡條件升級',
        condition:
          'Level 1: 持CUBE正卡，享指定消費2%回饋；Level 2: 持CUBE正卡、個人戶臺幣存款帳戶且登入CUBE App，享指定消費3%回饋；Level 3: 持CUBE正卡且為財富管理貴賓，享指定消費3.3%回饋',
        channel: '分級權益',
      },
      {
        type: FeatureType.DISCOUNT,
        feedback: '最高8.3%',
        max_feedback: '日本大丸百貨',
        simplified_condition: 'CUBE App領取優惠券',
        condition:
          '2025/1/15~2025/6/30期間，於日本大丸百貨福岡天神店消費，切換趣旅行權益方案享3.3%回饋，CUBE App領取優惠券再享5%回饋',
        channel: '海外消費',
      },
      {
        type: FeatureType.DISCOUNT,
        feedback: '最高8%',
        max_feedback: 'Expedia訂房',
        simplified_condition: 'CUBE App領取優惠券',
        condition:
          '2025/1/1~2025/12/31期間，透過Expedia x CUBE卡專屬網頁訂房，切換趣旅行權益方案享3%回饋，CUBE App領取優惠券再享5%回饋',
        channel: '旅遊',
      },
    ],
    domestic_feedback: '0.3%',
    international_feedback: '0.3%',
    specific_channel_feedback: '3.3%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.HSBC_CASHBACK]: {
    name: '滙豐現金回饋卡',
    summary: '海外消費2.5%回饋，指定通路最高5%',
    issuing_bank: BANK_NAMES[BANK_KEYS.HSBC],
    payment_network: 'VISA',
    picture: 'logo_hsbc-cashback.jpg',
    key: 'hsbc-cashback',
    link: cardLinkMap[CARD_KEYS.HSBC_CASHBACK].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.HSBC_CASHBACK].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '2.5% 現金回饋',
        max_feedback: '無上限',
        simplified_condition: '海外消費',
        condition: '海外消費享2.5%現金回饋，無上限',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 回饋',
        max_feedback: '每月最高回饋1,000元',
        simplified_condition: '指定餐飲、百貨公司',
        condition: '於指定餐飲、百貨公司消費享5%回饋，每月最高回饋1,000元',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.DINING],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1.5% 現金回饋',
        max_feedback: '無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享1.5%現金回饋，無上限',
        channel: '一般消費',
      },
    ],
    domestic_feedback: '1.5%',
    international_feedback: '2.5%',
    specific_channel_feedback: '5%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.HSBC_TRAVEL_INFINITE]: {
    name: '滙豐旅人無限卡',
    summary: '海外消費NT$10=1哩，哩程即時入點',
    issuing_bank: BANK_NAMES[BANK_KEYS.HSBC],
    payment_network: 'VISA',
    picture: 'logo_hsbc-travel-infinite.png',
    key: 'hsbc-travel-infinite',
    link: cardLinkMap[CARD_KEYS.HSBC_TRAVEL_INFINITE].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.HSBC_TRAVEL_INFINITE].applyUrl,
    features: [
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$10=1旅遊積分',
        max_feedback: '無上限',
        simplified_condition: '海外消費',
        condition:
          '海外消費（含網路交易）每NT$10可獲得1旅遊積分，可兑換航空哩程或飯店點數，回饋無上限',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$18=1旅遊積分',
        max_feedback: '無上限',
        simplified_condition: '國內消費',
        condition: '國內消費每NT$18可獲得1旅遊積分，可兑換航空哩程或飯店點數，回饋無上限',
        channel: '一般消費',
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '1:1起兑換哩程',
        max_feedback: '15家以上航空公司',
        simplified_condition: '即時入哩',
        condition: '旅遊積分最優1:1起兑換15家以上航空哩程，指定航空哩程兑換即時入哩，隨換隨用',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.MILEAGE],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '1:1起兑換飯店點數',
        max_feedback: '3大飯店集團',
        simplified_condition: '即時入點',
        condition: '旅遊積分可兑換3大飯店集團點數，兑換即時入點，即刻享受',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.HOTEL],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '無限次使用',
        simplified_condition: 'Dragon Pass',
        condition:
          '持卡人及一位同行賞客可享有Dragon Pass會員資格，享全球1,000多間機場貴賓室無限次使用',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '機場接送',
        max_feedback: '每年8次',
        simplified_condition: '單筆消費滿NT$12,000',
        condition: '刷卡單筆消費滿NT$12,000，可享每年8次機場接送服務',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRANSPORTATION],
      },
    ],
    domestic_feedback: 'NT$18=1哩',
    international_feedback: 'NT$10=1哩',
    specific_channel_feedback: '機場貴賓室',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.HSBC_TRAVEL_ONE_SIGNATURE]: {
    name: '滙豐旅人御璽卡',
    summary: '國內外消費NT$15=1哩，哩程即時入點',
    issuing_bank: BANK_NAMES[BANK_KEYS.HSBC],
    payment_network: 'VISA',
    picture: 'logo_hsbc-travel-one-signature.jpg',
    key: 'hsbc-travel-one-signature',
    link: cardLinkMap[CARD_KEYS.HSBC_TRAVEL_ONE_SIGNATURE].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.HSBC_TRAVEL_ONE_SIGNATURE].applyUrl,
    features: [
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$15=1旅遊積分',
        max_feedback: '無上限',
        simplified_condition: '國內外消費',
        condition: '國內外消費每NT$15可獲得1旅遊積分，可兑換航空哩程或飯店點數，回饋無上限',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.MILEAGE],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '1:1起兑換哩程',
        max_feedback: '15家以上航空公司',
        simplified_condition: '即時入哩',
        condition: '旅遊積分最優1:1起兑換15家以上航空哩程，指定航空哩程兑換即時入哩，隨換隨用',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.MILEAGE],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '1:1起兑換飯店點數',
        max_feedback: '3大飯店集團',
        simplified_condition: '即時入點',
        condition: '旅遊積分可兑換3大飯店集團點數，兑換即時入點，即刻享受',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.HOTEL],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: 'Agoda訂房優惠',
        max_feedback: '最高15%現折',
        simplified_condition: '指定專屬網站',
        condition: '於Agoda指定專屬網站預訂飯店，享全球訂房最高15%現折優惠',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '每年6次',
        simplified_condition: 'Dragon Pass',
        condition: '持卡人可享有Dragon Pass會員資格，每年可免費使用全球機場貴賓室6次',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '機場接送',
        max_feedback: '每年4次',
        simplified_condition: '單筆消費滿NT$10,000',
        condition: '刷卡單筆消費滿NT$10,000，可享每年4次機場接送服務',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRANSPORTATION],
      },
    ],
    domestic_feedback: 'NT$15=1哩',
    international_feedback: 'NT$15=1哩',
    specific_channel_feedback: '機場貴賓室',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.HSBC_TRAVEL_ONE]: {
    name: '滙豐旅人輕旅卡',
    summary: '國內外消費NT$20=1哩，哩程即時入點',
    issuing_bank: BANK_NAMES[BANK_KEYS.HSBC],
    payment_network: 'VISA',
    picture: 'logo_hsbc-travel-one.jpg',
    key: 'hsbc-travel-one',
    link: cardLinkMap[CARD_KEYS.HSBC_TRAVEL_ONE].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.HSBC_TRAVEL_ONE].applyUrl,
    features: [
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$20=1旅遊積分',
        max_feedback: '無上限',
        simplified_condition: '國內外消費',
        condition: '國內外消費每NT$20可獲得1旅遊積分，可兑換航空哩程或飯店點數，回饋無上限',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.MILEAGE],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '1:1起兑換哩程',
        max_feedback: '15家以上航空公司',
        simplified_condition: '即時入哩',
        condition: '旅遊積分最優1:1起兑換15家以上航空哩程，指定航空哩程兑換即時入哩，隨換隨用',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.MILEAGE],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '1:1起兑換飯店點數',
        max_feedback: '3大飯店集團',
        simplified_condition: '即時入點',
        condition: '旅遊積分可兑換3大飯店集團點數，兑換即時入點，即刻享受',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.HOTEL],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: 'Agoda訂房優惠',
        max_feedback: '最高13%現折',
        simplified_condition: '指定專屬網站',
        condition: '於Agoda指定專屬網站預訂飯店，享全球訂房最高13%現折優惠',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '每年4次',
        simplified_condition: 'Dragon Pass',
        condition: '持卡人可享有Dragon Pass會員資格，每年可免費使用全球機場貴賓室4次',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '機場接送',
        max_feedback: '每年1次',
        simplified_condition: '單筆消費滿NT$10,000',
        condition: '刷卡單筆消費滿NT$10,000，可享每年1次機場接送服務',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRANSPORTATION],
      },
    ],
    domestic_feedback: 'NT$20=1哩',
    international_feedback: 'NT$20=1哩',
    specific_channel_feedback: '機場貴賓室',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.ESUN_UNICARD]: {
    name: '玉山Unicard',
    summary: '百大特店最高5%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.ESUN],
    payment_network: 'VISA',
    picture: 'logo_esun-unicard.webp',
    key: 'esun-unicard',
    link: cardLinkMap[CARD_KEYS.ESUN_UNICARD].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.ESUN_UNICARD].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 玉山e point',
        max_feedback: '一般消費基本回饋',
        simplified_condition: '需申辦帳單e化及綁定玉山銀行臺幣帳戶自動扣繳',
        condition:
          '一般消費享0.3% 玉山e point回饋(需申辦帳單e化)，加碼享0.7% 玉山e point回饋(需同時申辦帳單e化及綁定玉山銀行臺幣帳戶自動扣繳)',
        channel: '一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3.5% 玉山e point',
        max_feedback: '百大特店加碼2%，每月上限500點',
        simplified_condition: '簡單選(核卡即享)',
        condition:
          '最高3.5% (一般消費1%無上限，百大特店加碼2%每月上限500點，新戶加碼0.5%上限500點)',
        channel: '百大特店',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '4% 玉山e point',
        max_feedback: '百大特店加碼2.5%，每月上限500點',
        simplified_condition: '任意選(玉山Wallet app 選定8家特店)',
        condition:
          '最高4% (一般消費1%無上限，百大特店加碼2.5%每月上限500點，新戶加碼0.5%上限500點)，中華航空、長榮航空、日本航空、台灣虎航、樂桃航空、酷航、立榮航空、華信航空、Trip.com、booking.com、Hotels.com、AsiaYo、Expedia、KKday、KLOOK、雄獅旅遊、可樂旅遊、東南旅行社',
        channel: '百大特店',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 玉山e point',
        max_feedback: '百大特店加碼3.5%，每月上限500點',
        simplified_condition: 'UP選(完成任務或以99點玉山e point訂閱)',
        condition:
          '最高5% (一般消費1%無上限，百大特店加碼3.5%每月上限500點，新戶加碼0.5%上限500點)',
        channel: '百大特店',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '1%',
    specific_channel_feedback: '5%',
    start_date: '2025-01-01',
    end_date: '2025-09-30',
  },
  [CARD_KEYS.CTBC_LINE]: {
    name: '中信LINE Pay卡',
    summary: '首刷禮最高3,000元',
    issuing_bank: BANK_NAMES[BANK_KEYS.CTBC],
    payment_network: 'VISA',
    picture: 'logo_ctbc-line.png',
    key: 'ctbc-line',
    link: cardLinkMap[CARD_KEYS.CTBC_LINE].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.CTBC_LINE].applyUrl,
    features: [
      {
        type: FeatureType.DISCOUNT,
        feedback: '3,000元 現金',
        max_feedback: '首刷禮3,000元',
        simplified_condition: '首刷禮',
        condition: '核卡後60天內消費滿20,000元，贈送3,000元刷卡金',
        channel: '首刷',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% LINE Points',
        max_feedback: '每月最高回饋600點',
        simplified_condition: 'LINE Pay消費',
        condition: 'LINE Pay消費享6% LINE Points回饋，每月最高回饋600點',
        channel: '行動支付',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1.5% LINE Points',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享1.5% LINE Points回饋，無上限',
        channel: '國內一般消費',
      },
    ],
    domestic_feedback: '1.5%',
    international_feedback: '1.5%',
    specific_channel_feedback: '6%',
    start_date: '2025-01-01',
    end_date: '2025-08-31',
  },
  [CARD_KEYS.ESUN_STARLUX_TITANIUM]: {
    name: '玉山星宇航空聯名鈦金卡',
    summary: '最優NT$10=1哩，星宇航空消費最高NT$5=1哩，年費低',
    issuing_bank: BANK_NAMES[BANK_KEYS.ESUN],
    payment_network: 'VISA',
    picture: 'logo_esun-starlux-titanium.png',
    key: 'esun-starlux-titanium',
    link: cardLinkMap[CARD_KEYS.ESUN_STARLUX_TITANIUM].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.ESUN_STARLUX_TITANIUM].applyUrl,
    features: [
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$20=1哩',
        max_feedback: '無上限',
        simplified_condition: '國外消費',
        condition: '國外消費每NT$20可獲得1哩星宇航空COSMILE哩程，活動期間2025/3/1~2025/12/31',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$28=1哩',
        max_feedback: '無上限',
        simplified_condition: '國內消費',
        condition: '國內消費每NT$28可獲得1哩星宇航空COSMILE哩程，活動期間2025/3/1~2025/12/31',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '生日月2倍哩程',
        max_feedback: '無上限',
        simplified_condition: '生日月消費',
        condition: '持卡人於生日月份刷卡消費可獲得2倍哩程回饋，活動期間2025/3/1~2025/12/31',
        channel: '生日月消費',
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場外圍停車',
        max_feedback: '每年4次',
        simplified_condition: '每次最多7天',
        condition: '每年最多4次，每次最多7天免費機場外圍停車服務，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '旅遊平安險',
        max_feedback: '最高2,000萬',
        simplified_condition: '公共運輸工具',
        condition:
          '支付旅途中全額公共運輸工具費用，或參加旅行團時刷卡支付80%以上之團費，享旅遊平安險最高2,000萬保障',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
    ],
    domestic_feedback: 'NT$28=1哩',
    international_feedback: 'NT$20=1哩',
    specific_channel_feedback: 'NT$20=1哩',
    start_date: '2025-03-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.ESUN_STARLUX_BUSINESS]: {
    name: '玉山星宇航空聯名商務鈦金卡',
    summary: '最優NT$15=1哩，星宇航空消費最高NT$5=1哩，商務旅行優惠',
    issuing_bank: BANK_NAMES[BANK_KEYS.ESUN],
    payment_network: 'VISA',
    picture: 'logo_esun-starlux-business.png',
    key: 'esun-starlux-business',
    link: cardLinkMap[CARD_KEYS.ESUN_STARLUX_BUSINESS].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.ESUN_STARLUX_BUSINESS].applyUrl,
    features: [
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$25=1哩',
        max_feedback: '無上限',
        simplified_condition: '國外消費',
        condition: '國外消費每NT$25可獲得1哩星宇航空COSMILE哩程，活動期間2025/3/1~2025/12/31',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$15=1哩',
        max_feedback: '無上限',
        simplified_condition: '國內消費',
        condition: '國內消費每NT$15可獲得1哩星宇航空COSMILE哩程，活動期間2025/3/1~2025/12/31',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '生日月2倍哩程',
        max_feedback: '無上限',
        simplified_condition: '生日月消費',
        condition: '持卡人於生日月份刷卡消費可獲得2倍哩程回饋，活動期間2025/3/1~2025/12/31',
        channel: '生日月消費',
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '每年2次',
        simplified_condition: '消費達門檻',
        condition: '每年最多2次免費使用機場貴賓室服務，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場外圍停車',
        max_feedback: '每年4次',
        simplified_condition: '每次最多7天',
        condition: '每年最多4次，每次最多7天免費機場外圍停車服務，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '旅遊平安險',
        max_feedback: '最高3,000萬',
        simplified_condition: '公共運輸工具',
        condition:
          '支付旅途中全額公共運輸工具費用，或參加旅行團時刷卡支付80%以上之團費，享旅遊平安險最高3,000萬保障',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
    ],
    domestic_feedback: 'NT$15=1哩',
    international_feedback: 'NT$25=1哩',
    specific_channel_feedback: 'NT$15=1哩',
    start_date: '2025-03-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.ESUN_STARLUX_WORLD]: {
    name: '玉山星宇航空聯名世界卡',
    summary: '最優NT$10=1哩，星宇航空消費最高NT$5=1哩，頂級旅遊禮遇',
    issuing_bank: BANK_NAMES[BANK_KEYS.ESUN],
    payment_network: 'VISA',
    picture: 'logo_esun-starlux-world.png',
    key: 'esun-starlux-world',
    link: cardLinkMap[CARD_KEYS.ESUN_STARLUX_WORLD].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.ESUN_STARLUX_WORLD].applyUrl,
    features: [
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$10=1哩',
        max_feedback: '無上限',
        simplified_condition: '國外消費',
        condition: '國外消費每NT$10可獲得1哩星宇航空COSMILE哩程，活動期間2025/3/1~2025/12/31',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$20=1哩',
        max_feedback: '無上限',
        simplified_condition: '國內消費',
        condition: '國內消費每NT$20可獲得1哩星宇航空COSMILE哩程，活動期間2025/3/1~2025/12/31',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '生日月2倍哩程',
        max_feedback: '無上限',
        simplified_condition: '生日月消費',
        condition: '持卡人於生日月份刷卡消費可獲得2倍哩程回饋，活動期間2025/3/1~2025/12/31',
        channel: '生日月消費',
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '每年12次',
        simplified_condition: '消費達門檻',
        condition: '每年最多12次免費使用機場貴賓室服務，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場接送',
        max_feedback: '每年4次',
        simplified_condition: '消費達門檻',
        condition: '每年最多4次免費機場接送服務，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場外圍停車',
        max_feedback: '不限次數',
        simplified_condition: '每次最多30天',
        condition: '每年不限次數，每次最多30天免費機場外圍停車服務，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '旅遊平安險',
        max_feedback: '最高5,000萬',
        simplified_condition: '公共運輸工具',
        condition:
          '支付旅途中全額公共運輸工具費用，或參加旅行團時刷卡支付80%以上之團費，享旅遊平安險最高5,000萬保障',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
    ],
    domestic_feedback: 'NT$20=1哩',
    international_feedback: 'NT$10=1哩',
    specific_channel_feedback: 'NT$10=1哩',
    start_date: '2025-03-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.ESUN_STARLUX_WORLD_SUPREME]: {
    name: '玉山星宇航空聯名世界之極卡',
    summary: '最優NT$10=1哩，星宇航空消費最高NT$5=1哩，邀請制頂級卡',
    issuing_bank: BANK_NAMES[BANK_KEYS.ESUN],
    payment_network: 'VISA',
    picture: 'logo_esun-starlux-world-supreme.png',
    key: 'esun-starlux-world-supreme',
    link: cardLinkMap[CARD_KEYS.ESUN_STARLUX_WORLD_SUPREME].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.ESUN_STARLUX_WORLD_SUPREME].applyUrl,
    features: [
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$10=1哩',
        max_feedback: '無上限',
        simplified_condition: '國外消費',
        condition: '國外消費每NT$10可獲得1哩星宇航空COSMILE哩程，活動期間2025/3/1~2025/12/31',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.MILEAGE,
        feedback: 'NT$15=1哩',
        max_feedback: '無上限',
        simplified_condition: '國內消費',
        condition: '國內消費每NT$20可獲得1哩星宇航空COSMILE哩程，活動期間2025/3/1~2025/12/31',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.MILEAGE,
        feedback: '生日月2倍哩程',
        max_feedback: '無上限',
        simplified_condition: '生日月消費',
        condition: '持卡人於生日月份刷卡消費可獲得2倍哩程回饋，活動期間2025/3/1~2025/12/31',
        channel: '生日月消費',
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '每年18次',
        simplified_condition: '消費達門檻',
        condition: '每年最多18次免費使用機場貴賓室服務，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場接送',
        max_feedback: '每年8次',
        simplified_condition: '消費達門檻',
        condition: '每年最多8次免費機場接送服務，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場外圍停車',
        max_feedback: '不限次數',
        simplified_condition: '每次最多30天',
        condition: '每年不限次數，每次最多30天免費機場外圍停車服務，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '旅遊平安險',
        max_feedback: '最高5,000萬',
        simplified_condition: '公共運輸工具',
        condition:
          '支付旅途中全額公共運輸工具費用，或參加旅行團時刷卡支付80%以上之團費，享旅遊平安險最高5,000萬保障',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '全球禮賓秘書',
        max_feedback: '24小時服務',
        simplified_condition: '世界之極卡專屬',
        condition: '提供24小時全球禮賓秘書服務，包含旅遊安排、餐廳預訂、禮品採購等專屬服務',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '全球急難救援',
        max_feedback: '緊急醫療協助',
        simplified_condition: '世界之極卡專屬',
        condition: '提供全球急難救援服務，包含緊急醫療協助、旅遊緊急支援等',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
    ],
    domestic_feedback: 'NT$15=1哩',
    international_feedback: 'NT$10=1哩',
    specific_channel_feedback: 'NT$10=1哩',
    start_date: '2025-03-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.SINO_GREEN]: {
    name: '永豐現金回饋Green卡',
    summary: '精選綠色通路最高5%現金回饋，國外2%、國內1%回饋無上限',
    issuing_bank: BANK_NAMES[BANK_KEYS.SINO],
    payment_network: 'VISA',
    picture: 'logo_sino-green.png',
    key: CARD_KEYS.SINO_GREEN,
    link: cardLinkMap[CARD_KEYS.SINO_GREEN].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.SINO_GREEN].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '1%現金回饋',
        max_feedback: '無上限',
        simplified_condition: '國內一般消費',
        condition: '國內一般消費享1%現金回饋，無上限，活動期間2025/1/1~2025/6/30',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2%現金回饋',
        max_feedback: '無上限',
        simplified_condition: '國外一般消費',
        condition: '國外一般消費享2%現金回饋，無上限，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5%現金回饋',
        max_feedback: '每月上限300元',
        simplified_condition: '精選綠色通路消費',
        condition:
          '精選國內綠色通路消費最高5%現金回饋（含卡片一般消費基本回饋1%，當期帳單消費，次期帳單回饋），活動期間2025/1/1~2025/6/30',
        channel: '綠色環保消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1.2%現金回饋',
        max_feedback: '無上限',
        simplified_condition: '保費代扣繳',
        condition: '保費代扣繳享1.2%現金回饋，無上限，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.INSURANCE],
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '種一棵樹',
        max_feedback: '辦卡即享',
        simplified_condition: '辦卡刷永豐',
        condition: '辦卡刷永豐就種一棵樹，永續環保，活動期間2025/1/1~2025/6/30',
        channel: '綠色環保消費',
      },
    ],
    domestic_feedback: '1%現金回饋',
    international_feedback: '2%現金回饋',
    specific_channel_feedback: '5%現金回饋',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.SINO_DUE_CURRENCY]: {
    name: '永豐幣倍卡',
    summary: '雙幣信用卡，國外2%回饋存入外幣帳戶，新戶完成任務最高12%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.SINO],
    payment_network: 'VISA',
    picture: 'logo_sino-due-currency.png',
    key: CARD_KEYS.SINO_DUE_CURRENCY,
    link: cardLinkMap[CARD_KEYS.SINO_DUE_CURRENCY].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.SINO_DUE_CURRENCY].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '1%現金回饋',
        max_feedback: '無上限',
        simplified_condition: '國內一般消費',
        condition: '國內一般消費享1%現金回饋，無上限，活動期間2025/1/1~2025/6/30',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2%現金回饋',
        max_feedback: '無上限',
        simplified_condition: '國外一般消費',
        condition:
          '國外一般消費享2%現金回饋，無上限，回饋金存入外幣帳戶，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '12%現金回饋',
        max_feedback: '指定通路',
        simplified_condition: '新戶完成任務',
        condition:
          '新戶完成任務最高可享12%回饋（行動支付、海外實體消費、指定網購、旅遊通路），活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.ONLINE_SHOPPING],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1.2%現金回饋',
        max_feedback: '無上限',
        simplified_condition: '保費代扣繳',
        condition: '保費代扣繳享1.2%現金回饋，無上限，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.INSURANCE],
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '線上換匯最高減6分',
        max_feedback: '無上限',
        simplified_condition: '幣倍卡專屬',
        condition: '幣倍卡專屬線上換匯最高減6分優惠，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '每月8次免ATM跨行手續費',
        max_feedback: '每月8次',
        simplified_condition: '幣倍金融信用卡專屬',
        condition: '幣倍金融信用卡享每月8次免ATM跨行手續費，活動期間2025/1/1~2025/6/30',
        channel: '金融服務',
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室/餐廳服務',
        max_feedback: '每年2次',
        simplified_condition: '消費達門檻',
        condition: '龍騰卡機場貴賓室/機場餐廳服務，每年2次，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場週邊停車服務',
        max_feedback: '每年不限次數',
        simplified_condition: '每次最多8天',
        condition: '機場週邊停車服務，每年不限次數，每次最多8天，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '旅遊平安險',
        max_feedback: '最高2,000萬',
        simplified_condition: '公共運輸工具',
        condition: '支付旅途中全額公共運輸工具費用，享旅遊平安險最高2,000萬保障',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
    ],
    domestic_feedback: '1%現金回饋',
    international_feedback: '2%現金回饋',
    specific_channel_feedback: '12%現金回饋',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.SINO_CASHCARD_JCB]: {
    name: '永豐現金回饋JCB卡',
    summary: '特選通路最高5%回饋，國外2%、國內1%回饋無上限',
    issuing_bank: BANK_NAMES[BANK_KEYS.SINO],
    payment_network: 'JCB',
    picture: 'logo_sino-cashcard-jcb.jpg',
    key: CARD_KEYS.SINO_CASHCARD_JCB,
    link: cardLinkMap[CARD_KEYS.SINO_CASHCARD_JCB].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.SINO_CASHCARD_JCB].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '1%現金回饋',
        max_feedback: '無上限',
        simplified_condition: '國內一般消費',
        condition: '國內一般消費享1%現金回饋，無上限，活動期間2025/1/1~2025/6/30',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2%現金回饋',
        max_feedback: '無上限',
        simplified_condition: '國外一般消費',
        condition: '國外一般消費享2%現金回饋，無上限，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5%現金回饋',
        max_feedback: '每月上限300元',
        simplified_condition: '特選通路消費',
        condition:
          '特選通路最高5%回饋（含基本回饋，加碼3%回饋每月上限300元），活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.ONLINE_SHOPPING],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5%現金回饋',
        max_feedback: '每月上限300元',
        simplified_condition: '日本消費滿額',
        condition: '日本消費滿額再加碼5%，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1%現金回饋',
        max_feedback: '無上限',
        simplified_condition: '保費代扣繳',
        condition: '保費代扣繳1%回饋無上限，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.INSURANCE],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '旅遊平安險',
        max_feedback: '最高2,000萬',
        simplified_condition: '公共運輸工具',
        condition: '支付旅途中全額公共運輸工具費用，享旅遊平安險最高2,000萬保障',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場外圍停車',
        max_feedback: '每年不限次數',
        simplified_condition: '消費達門檻',
        condition: '機場外圍停車服務，每年不限次數，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
    ],
    domestic_feedback: '1%現金回饋',
    international_feedback: '2%現金回饋',
    specific_channel_feedback: '5%現金回饋',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.SINO_SPORT]: {
    name: '永豐SPORT卡',
    summary: '運動賺回饋，一般消費最高2%豐點，指定支付/通路最高6%豐點',
    issuing_bank: BANK_NAMES[BANK_KEYS.SINO],
    payment_network: 'VISA',
    picture: 'logo_sino-sport.png',
    key: CARD_KEYS.SINO_SPORT,
    link: cardLinkMap[CARD_KEYS.SINO_SPORT].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.SINO_SPORT].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '1%豐點回饋',
        max_feedback: '無上限',
        simplified_condition: '基本回饋',
        condition:
          '下載註冊汗水不白流APP(勾選卡友)，刷永豐SPORT卡享基本1%豐點回饋，活動期間2025/1/1~2025/6/30',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2%豐點回饋',
        max_feedback: '無上限',
        simplified_condition: '運動獎勵',
        condition:
          '運動獎勵加碼1%豐點，需於消費當月於《汗水不白流APP》達10,000大卡或Apple Watch圓滿畫圃10次，活動期間2025/1/1~2025/6/30',
        channel: '運動健身',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '6%豐點回饋',
        max_feedback: '指定支付/通路',
        simplified_condition: '指定支付/通路加碼',
        condition:
          '指定支付/通路加碼4%豐點，需於消費當月於《汗水不白流APP》達10,000大卡或Apple Watch圓滿畫圃10次，活動期間2025/1/1~2025/6/30',
        channel: '行動支付',
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '行動支付優惠',
        max_feedback: 'Apple Pay/Google Pay',
        simplified_condition: '指定支付通路',
        condition:
          '一般消費綁定Apple Pay／Google Pay／Samsung Pay／Garmin Pay支付享加碼回饋，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.MOBILE_PAYMENT],
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '運動健身優惠',
        max_feedback: 'World Gym等健身中心',
        simplified_condition: '指定健身通路',
        condition:
          'World Gym、健身工廠、TRUE YOGA FITNESS、Curves、台北市運動中心、新北市運動中心等指定健身通路享加碼回饋，活動期間2025/1/1~2025/6/30',
        channel: '運動健身',
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '醫藥保健優惠',
        max_feedback: '屏臺氏等藥局',
        simplified_condition: '指定醫藥通路',
        condition:
          '屏臺氏、康是美、寶雅、好心股健檢中心等指定醫藥通路享加碼回饋，活動期間2025/1/1~2025/6/30',
        channel: '醫療保健',
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '電競娛樂優惠',
        max_feedback: 'App Store等平台',
        simplified_condition: '指定電競通路',
        condition:
          'App Store、Google Play商店應用程式、Nintendo、PlayStation、Steam等指定電競通路享加碼回饋，活動期間2025/1/1~2025/6/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.ENTERTAINMENT],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場外圍停車',
        max_feedback: '每年不限次數',
        simplified_condition: '消費達門檻',
        condition: '機場外圍停車服務，每年不限次數，須達消費門檻',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
    ],
    domestic_feedback: '1%豐點回饋',
    international_feedback: '1%豐點回饋',
    specific_channel_feedback: '6%豐點回饋',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.SINO_MITSUI]: {
    name: '永豐MITSUI OUTLET PARK聯名卡',
    summary: '館內消費最高1%豐點回饋，館外0.3%回饋，日本購物最高27%折扣',
    issuing_bank: BANK_NAMES[BANK_KEYS.SINO],
    payment_network: 'VISA',
    picture: 'logo_sino-mitsui.png',
    key: CARD_KEYS.SINO_MITSUI,
    link: cardLinkMap[CARD_KEYS.SINO_MITSUI].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.SINO_MITSUI].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '0.5-1%豐點回饋',
        max_feedback: '館內消費',
        simplified_condition: '館內消費金額不同',
        condition:
          '館內消費：1-29,999元享0.5%豐點回饋；30,000元享1%豐點回饋，活動期間2025/1/1– 2025/06/30',
        channel: '百貨購物',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '0.3%豐點回饋',
        max_feedback: '館外消費',
        simplified_condition: '館外一般消費',
        condition: '館外消費享0.3%豐點回饋，活動期間2025/1/1– 2025/06/30',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.DISCOUNT,
        feedback: '日本購物最高27%折扣',
        max_feedback: '日本地區',
        simplified_condition: '日本地區消費',
        condition: '日本購物最高27%折扣，活動期間2025/1/1– 2025/06/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '免費停車及購物娛樂85折起',
        max_feedback: '三井專屬',
        simplified_condition: '三井卡專屬',
        condition: '免費停車及購物娛樂85折起，活動期間2025/1/1– 2025/06/30',
        channel: '百貨購物',
      },
      {
        type: FeatureType.DISCOUNT,
        feedback: '館外餐飲10%優惠',
        max_feedback: '需登錄',
        simplified_condition: '三井卡專屬',
        condition: '2025年H1三井卡館外餐飲10%優惠（需登錄），活動期間2025/1/1– 2025/06/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.DINING],
      },
      {
        type: FeatureType.DISCOUNT,
        feedback: '和苑三井花園飯店住宿優惠',
        max_feedback: '台北忠孝',
        simplified_condition: '三井卡專屬',
        condition: '和苑三井花園飯店 台北忠孝住宿優惠，活動期間2025/1/1– 2025/06/30',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.HOTEL],
      },
    ],
    domestic_feedback: '0.3-1%豐點回饋',
    international_feedback: '0.3%豐點回饋',
    specific_channel_feedback: '1%豐點回饋',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
};
